import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/free-regular-svg-icons';
import {
  faCloudArrowDown,
  faCompressArrowsAlt,
  faDrawPolygon,
  faFileArrowUp,
  faWalking,
} from '@fortawesome/free-solid-svg-icons';
import { useCustomAreaCreation } from '../../../../hooks/SimViewer/useCustomAreaCreation';
import { useCustomAreas } from '../../../../hooks/SimViewer/useCustomAreas';
import { useCustomAreaEdition } from '../../../../hooks/SimViewer/useCustomAreaEdition';
import { useMapSelection } from '../../../../hooks/SimViewer/useMapSelection';
import { useDataFilter } from '../../../../hooks/SimViewer/useDataFilter';
import type { CustomArea } from '../../../../types/domain';
import { CustomAreaItem } from './CustomAreaItem';
import { InfoPopoverButton } from '../../../InfoPopoverButton';
import { CustomAreaSelectModal } from './CustomAreaSelectModal';
import { CustomAreaUploadModal } from './CustomAreaUploadModal';
import { CustomAreaSaveModal } from './CustomAreaSaveModal';
import { CustomAreaEditModal } from './CustomAreaEditModal';
import { CustomAreaCreateModal } from './CustomAreaCreateModal';

type Props = {
  menuOpen: boolean;
  toggleMenuOpen: () => void;
};

const CustomAreaSection = ({ menuOpen, toggleMenuOpen }: Props): ReactNode => {
  const { t } = useTranslation();
  const { creatingCustomArea, startCustomAreaCreation, endCustomAreaCreation } =
    useCustomAreaCreation();
  const { customAreas, deleteCustomArea } = useCustomAreas();
  const {
    editingCustomArea,
    startCustomAreaEdition,
    endCustomAreaEdition,
    resetCustomArea,
  } = useCustomAreaEdition();
  const { selectionKind } = useMapSelection();
  const { deleteCustomAreaFilter } = useDataFilter();

  const [isLoadModalOpen, setIsLoadModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [savingCustomArea, setSavingCustomArea] = useState<CustomArea | null>(
    null
  );

  useEffect((): void | (() => void) => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        if (creatingCustomArea) {
          endCustomAreaCreation();
        }
        if (editingCustomArea) {
          resetCustomArea();
          endCustomAreaEdition();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return (): void => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    creatingCustomArea,
    editingCustomArea,
    endCustomAreaCreation,
    endCustomAreaEdition,
    resetCustomArea,
  ]);

  if (!menuOpen) {
    return (
      <Button
        size="large"
        icon={
          <FontAwesomeIcon icon={faDrawPolygon} size="lg" color="#707070" />
        }
        onClick={toggleMenuOpen}
      />
    );
  }
  return (
    <>
      <Section>
        <SectionHeader>
          <FontAwesomeIcon icon={faDrawPolygon} size="lg" color="#707070" />
          <HeaderLabel>
            {t('customArea')}
            <InfoPopoverButton content={t('additionalInfos.customArea')} />
          </HeaderLabel>
          <HeaderAction>
            <Button
              icon={
                <FontAwesomeIcon
                  icon={faCompressArrowsAlt}
                  size="sm"
                  color="#9D9D9D"
                />
              }
              type="text"
              shape="circle"
              size="small"
              onClick={toggleMenuOpen}
            />
          </HeaderAction>
        </SectionHeader>
        <SectionItem>
          <SectionItemLabel>{t('create')}</SectionItemLabel>
          <SectionItemContent>
            <ButtonList>
              <IconButton
                type="text"
                data-active={creatingCustomArea?.type === 'polygon'}
                disabled={
                  selectionKind !== 'none' ||
                  ['circle', 'walking'].includes(
                    creatingCustomArea?.type || ''
                  ) ||
                  !!editingCustomArea
                }
                onClick={(event: MouseEvent<HTMLButtonElement>): void => {
                  if (creatingCustomArea?.type !== 'polygon') {
                    startCustomAreaCreation('polygon');
                  } else {
                    endCustomAreaCreation();

                    // クリック後もフォーカスが残ることがあるため、強制的にフォーカスを外す
                    event.currentTarget.blur();
                  }
                }}
              >
                <FontAwesomeIcon icon={faDrawPolygon} size="lg" />
                {t('polygon')}
              </IconButton>
              <IconButton
                type="text"
                data-active={creatingCustomArea?.type === 'circle'}
                disabled={
                  selectionKind !== 'none' ||
                  ['polygon', 'walking'].includes(
                    creatingCustomArea?.type || ''
                  ) ||
                  !!editingCustomArea
                }
                onClick={(event: MouseEvent<HTMLButtonElement>): void => {
                  if (creatingCustomArea?.type !== 'circle') {
                    startCustomAreaCreation('circle');
                  } else {
                    endCustomAreaCreation();

                    // クリック後もフォーカスが残ることがあるため、強制的にフォーカスを外す
                    event.currentTarget.blur();
                  }
                }}
              >
                <FontAwesomeIcon icon={faCircle} size="lg" />
                {t('circle')}
              </IconButton>
              <IconButton
                type="text"
                data-active={creatingCustomArea?.type === 'walking'}
                disabled={
                  selectionKind !== 'none' ||
                  ['polygon', 'circle'].includes(
                    creatingCustomArea?.type || ''
                  ) ||
                  !!editingCustomArea
                }
                style={{ position: 'relative' }}
                onClick={(event: MouseEvent<HTMLButtonElement>): void => {
                  if (creatingCustomArea?.type !== 'walking') {
                    startCustomAreaCreation('walking');
                  } else {
                    endCustomAreaCreation();

                    // クリック後もフォーカスが残ることがあるため、強制的にフォーカスを外す
                    event.currentTarget.blur();
                  }
                }}
              >
                <FontAwesomeIcon icon={faWalking} size="lg" />
                {t('walkingDistance')}
                <InfoPopoverButton
                  content={t('additionalInfos.walkingDistance', {
                    returnObjects: true,
                  })}
                  style={{ position: 'absolute', top: '-4px', right: '-4px' }}
                />
              </IconButton>
            </ButtonList>
          </SectionItemContent>
        </SectionItem>
        <SectionItem>
          <SectionItemLabel>{t('loading')}</SectionItemLabel>
          <SectionItemContent>
            <ButtonList>
              <IconButton
                type="text"
                disabled={
                  selectionKind !== 'none' ||
                  !!creatingCustomArea ||
                  !!editingCustomArea
                }
                onClick={(): void => {
                  setIsLoadModalOpen(true);
                }}
              >
                <FontAwesomeIcon icon={faCloudArrowDown} size="lg" />
                {t('saved')}
              </IconButton>
              <IconButton
                type="text"
                disabled={
                  selectionKind !== 'none' ||
                  !!creatingCustomArea ||
                  !!editingCustomArea
                }
                onClick={(): void => {
                  setIsImportModalOpen(true);
                }}
              >
                <FontAwesomeIcon icon={faFileArrowUp} size="lg" />
                {t('file')}
              </IconButton>
            </ButtonList>
          </SectionItemContent>
        </SectionItem>
        <SectionItem>
          <SectionItemLabel full>{t('list')}</SectionItemLabel>
          <SectionItemContent>
            {customAreas.length === 0 && (
              <EmptyState>{t('message.noCustomAreaDisplayed')}</EmptyState>
            )}
            {customAreas.map((customArea: CustomArea): ReactNode => {
              return (
                <CustomAreaItem
                  key={customArea.key}
                  customArea={customArea}
                  onClickSave={(): void => {
                    setSavingCustomArea(customArea);
                  }}
                  onClickEdit={(): void => {
                    if (editingCustomArea?.key === customArea.key) {
                      resetCustomArea();
                      endCustomAreaEdition();
                    } else {
                      startCustomAreaEdition(customArea);
                    }
                  }}
                  onClickDelete={(): void => {
                    deleteCustomArea(customArea.key);
                    deleteCustomAreaFilter(customArea.key, 'all');
                  }}
                />
              );
            })}
          </SectionItemContent>
        </SectionItem>
      </Section>
      <CustomAreaSelectModal
        isOpen={isLoadModalOpen}
        onClose={(): void => {
          setIsLoadModalOpen(false);
        }}
      />
      <CustomAreaUploadModal
        isOpen={isImportModalOpen}
        onClose={(): void => {
          setIsImportModalOpen(false);
        }}
      />
      <CustomAreaSaveModal
        key={savingCustomArea?.key}
        customArea={savingCustomArea}
        isOpen={!!savingCustomArea}
        onClose={(): void => {
          setSavingCustomArea(null);
        }}
      />
      <CustomAreaCreateModal
        key={creatingCustomArea?.type}
        customAreaType={creatingCustomArea?.type || null}
        isOpen={!!creatingCustomArea}
      />
      <CustomAreaEditModal
        key={editingCustomArea?.key}
        customAreaKey={editingCustomArea?.key || null}
        customAreaType={editingCustomArea?.type || null}
        isOpen={!!editingCustomArea}
      />
    </>
  );
};

const Section = styled.div`
  width: 100%;
  background: #fafafa;
  padding: 0 0 8px 0;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid #d9d9d9;
`;

const HeaderLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  line-height: 16px;
`;

const HeaderAction = styled.div`
  margin-left: auto;
`;

const SectionItem = styled.div`
  margin: 8px 8px 0 8px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
`;

const SectionItemLabel = styled.div<{ full?: boolean }>`
  line-height: 1.3;
  font-size: 12px;
  font-weight: 600;
  flex: ${(props) => (props.full ? '1 1 100%' : '0 1 auto')};
`;

const SectionItemContent = styled.div`
  flex: 1 1 auto;
  min-width: 50%;
  text-align: right;
`;

const ButtonList = styled.div`
  min-width: 200px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 8px;
`;

const IconButton = styled(Button)`
  padding: 4px;
  height: auto;
  width: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  & span {
    font-size: 12px;
    text-wrap: balance;
    text-align: center;
    line-height: 1;
  }
  &[data-active='true'] {
    border: 1.5px solid #000000d9;
    background-color: #d9d9d9a6;
  }
  &:hover:not([data-active='true']):not(:disabled) {
    background-color: #d9d9d9a6;
  }
  &:disabled,
  &:disabled:hover {
    color: #646464;
  }
`;

const EmptyState = styled.p`
  font-size: 12px;
  color: #8c8c8c;
  margin: 4px 0;
`;

export { CustomAreaSection };
